import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import { max, min, nonNullable } from 'lib/array/arrayUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { isBedbankOffer, isLEOffer } from 'lib/offer/offerTypes'
import type { Moment } from 'moment'
import moment from 'moment'
import qs from 'qs'
import type { IntlShape } from 'react-intl'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export interface FlightSearchLinkParams {
  originAirportCode: string
  originAirportName: string | undefined
  destinationAirportCode: string
  destinationAirportName: string | undefined
  departDate: string
  returnDate: string
  adults: number
  children?: number
  infants?: number
  fareClass: string
  fareType: string
  maxArrivalTime?: string
  minReturningDepartureTime?: string
  source?: string
  searchId?: number
}

export function selectOrderItem(order: App.Order) {
  return order.items[0] // Working only for single hotels orders, we might change this in the future
}

export function selectBedbankOrderItem(order: App.Order): App.OrderBedbankItem {
  return order.bedbankItems[0]
}

export function getDateLabel(order: App.Order) {
  const item = selectOrderItem(order) // Working only for single hotels orders
  if (!item.reservation || item.reservationType === 'buy_now_book_later') return null
  const intl = getCurrentIntl()
  const startDateLabel = intlDateFormatter(
    item.reservation?.startDate,
    INTL_DAY_MONTH_NAME_SHORT,
    intl.locale,
  )
  const endDateLabel = intlDateFormatter(
    item.reservation?.endDate,
    INTL_DMY_CASUAL_SHORT_FORMAT,
    intl.locale,
  )
  return intl.formatMessage(
    {
      id: '6XCUTJjkn5OSj-1icJZw',
      defaultMessage: '{dateRange} · {durationLabel}',
      description: 'Booking details date range and duration label on the checkout confirmation page',
    },
    {
      dateRange: intl.formatMessage(
        {
          id: 'hOEY5DimyicycGPsVN2p',
          defaultMessage: '{startDate} - {endDate}',
          description: 'Date range label shown with booking details on the checkout confirmation page',
        },
        {
          startDate: startDateLabel,
          endDate: endDateLabel,
        },
      ),
      durationLabel: item.durationLabel,
    },
  )
}

export function getOfferCountry(offer: App.BedbankOffer | App.HotelOffer) {
  if (isLEOffer(offer)) {
    return {
      countryName: offer.property.geoData.country,
      countryCode: offer.property.geoData.countryCode,
    }
  }
  if (isBedbankOffer(offer)) {
    return {
      countryName: offer.property.address.countryName,
      countryCode: offer.property.address.countryCode,
    }
  }
}

export function getUpsellFlightLink(startDate?: string, endDate?: string, occupants?: App.Occupants, originAirport?: App.Airport, destinationAirport?: App.Airport, flightsMaxArrivalTime?: string, flightsMinReturningDepartureTime?: string) {
  if (!originAirport || !destinationAirport || !startDate || !endDate || !occupants) return '/flights'

  const flightSearchParams: FlightSearchLinkParams = {
    originAirportCode: originAirport.code,
    originAirportName: originAirport.name,
    destinationAirportCode: destinationAirport.code,
    destinationAirportName: destinationAirport.name,
    departDate: startDate,
    returnDate: endDate,
    adults: occupants.adults,
    children: occupants.children,
    infants: occupants.infants,
    fareClass: 'economy',
    fareType: 'return',
    // This ensures the hotel checkin/out times are respected if set on the offer
    maxArrivalTime: flightsMaxArrivalTime,
    minReturningDepartureTime: flightsMinReturningDepartureTime,
    // This param ensures the depart/return dates are treated as hotel check in/out dates
    source: 'hotel',
    // searchId: moment().unix(),
  }

  return `/flights-search-results?${qs.stringify(flightSearchParams)}`
}

/**
 * Returns the earliest start date and latest end date across all order items.
 * Returns ISO date strings (YYYY-MM-DD) to avoid timezone conversion issues.
 */
export function getOrderDateRange(order: App.Order, includeCancelled = false): { earliestDate: string | undefined, latestDate: string | undefined } {
  const allOrderItemStartDates = nonNullable([
    ...order.items.filter(item => (includeCancelled || item.status !== 'cancelled') && item.reservation).map(item => item.reservation?.startDate),
    ...order.bedbankItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.checkIn.format(ISO_DATE_FORMAT)),
    ...order.tourItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.tour.startDate),
    ...order.experienceItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.date),
    ...order.flightItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.departureDate),
    ...order.transferItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.date),
    ...order.carHireItems.filter(item => item.status !== 'failed' && (includeCancelled || item.status !== 'cancelled')).map(item => item.reservation?.pickUp.date),
    ...order.cruiseItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.departureDate),
  ])

  const allOrderItemEndDates = nonNullable([
    ...order.items.filter(item => (includeCancelled || item.status !== 'cancelled') && item.reservation).map(item => item.reservation?.endDate),
    ...order.bedbankItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.checkOut.format(ISO_DATE_FORMAT)),
    ...order.tourItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.tour.endDate),
    ...order.experienceItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.date),
    ...order.flightItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.arrivalDate),
    ...order.transferItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.date),
    ...order.carHireItems.filter(item => item.status !== 'failed' && (includeCancelled || item.status !== 'cancelled')).map(item => item.reservation?.dropOff.date),
    ...order.cruiseItems.filter(item => includeCancelled || item.status !== 'cancelled').map(item => item.arrivalDate),
  ])

  // ISO date strings sort lexicographically correctly
  const earliestDate = min(allOrderItemStartDates)
  const latestDate = max(allOrderItemEndDates)

  return {
    earliestDate,
    latestDate,
  }
}

export function getDaysToGoLabel(intl: IntlShape, checkinDate?: string | Moment, duration?: number): string {
  if (!checkinDate) return ''

  const checkIn = moment(checkinDate).startOf('day')
  const daysToGo = checkIn.diff(moment().startOf('day'), 'days')

  // Before stay
  if (daysToGo > 21) {
    return intl.formatMessage(
      {
        id: 'NoRtraCD2yAoJoZ8FTcG',
        defaultMessage: '{weekCount, plural, one {# week to go!} other {# weeks to go!}}',
        description: 'Weeks remaining until check-in for booking countdown label',
      },
      { weekCount: Math.floor(daysToGo / 7) },
    )
  } else if (daysToGo <= 21 && daysToGo > 1) {
    return intl.formatMessage(
      {
        id: 'VDUOY6BON7oSAEH_F3Id',
        defaultMessage: '{dayCount, plural, one {# day to go!} other {# days to go!}}',
        description: 'Days remaining until check-in for booking countdown label',
      },
      { dayCount: daysToGo },
    )
  } else if (daysToGo === 1) {
    return intl.formatMessage({
      id: 'eZpW0EgD6k4Imz29INY_',
      defaultMessage: 'Check-in tomorrow',
      description: 'Label shown when hotel check-in is tomorrow on the checkout confirmation page',
    })
  } else if (daysToGo === 0) {
    return intl.formatMessage({
      id: '4wBvDvZ27PijaMZgW8uF',
      defaultMessage: 'Check-in today',
      description: 'Label shown when hotel check-in is today on the checkout confirmation page',
    })
  }

  if (!duration) {
    return ''
  } else if (Math.abs(daysToGo) === (duration || 0) - 1) {
    return intl.formatMessage({
      id: 'KVgiCouuWvICx9z3rPeR',
      defaultMessage: 'Check-out tomorrow',
      description: 'Label shown when hotel check-out is tomorrow on the checkout confirmation page',
    })
  } else if (Math.abs(daysToGo) === (duration || 0)) {
    return intl.formatMessage({
      id: 'JSsU0Xegf-dykNMTtaUl',
      defaultMessage: 'Check-out today',
      description: 'Label shown when hotel check-out is today on the checkout confirmation page',
    })
  } else if (Math.abs(daysToGo) < (duration || 0)) {
    return intl.formatMessage({
      id: 'I8F4d9S5k2ppgiu8KL7K',
      defaultMessage: 'Enjoy your stay',
      description: 'Label shown during the stay period of a hotel booking',
    })
  }

  return ''
}
